import { supabase } from './supabase';
import type { ForumCategory, ForumPost, ForumComment, ForumLike, ForumBookmark } from './types/forum';

// Catégories
export async function getForumCategories() {
  const { data, error } = await supabase
    .from('forum_categories')
    .select('*')
    .order('name');

  if (error) throw error;
  return data;
}

// Publications
export async function getForumPosts(categorySlug?: string, page = 1, limit = 10) {
  let query = supabase
    .from('forum_posts')
    .select(`
      *,
      profiles (display_name, photo_url),
      forum_categories (*)
    `)
    .order('is_pinned', { ascending: false })
    .order('created_at', { ascending: false })
    .range((page - 1) * limit, page * limit);

  if (categorySlug) {
    query = query.eq('forum_categories.slug', categorySlug);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function getForumPost(postId: string) {
  const { data, error } = await supabase
    .from('forum_posts')
    .select(`
      *,
      profiles (display_name, photo_url),
      forum_categories (*)
    `)
    .eq('id', postId)
    .single();

  if (error) throw error;
  return data;
}

export async function createForumPost(post: Partial<ForumPost>) {
  const { data, error } = await supabase
    .from('forum_posts')
    .insert([post])
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateForumPost(postId: string, updates: Partial<ForumPost>) {
  const { data, error } = await supabase
    .from('forum_posts')
    .update(updates)
    .eq('id', postId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteForumPost(postId: string) {
  const { error } = await supabase
    .from('forum_posts')
    .delete()
    .eq('id', postId);

  if (error) throw error;
}

// Commentaires
export async function getPostComments(postId: string) {
  const { data, error } = await supabase
    .from('forum_comments')
    .select(`
      *,
      profiles (display_name, photo_url)
    `)
    .eq('post_id', postId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data;
}

export async function createComment(comment: Partial<ForumComment>) {
  const { data, error } = await supabase
    .from('forum_comments')
    .insert([comment])
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateComment(commentId: string, content: string) {
  const { data, error } = await supabase
    .from('forum_comments')
    .update({ content })
    .eq('id', commentId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteComment(commentId: string) {
  const { error } = await supabase
    .from('forum_comments')
    .delete()
    .eq('id', commentId);

  if (error) throw error;
}

// Likes
export async function toggleLike(userId: string, postId?: string, commentId?: string) {
  const { data: existingLike, error: checkError } = await supabase
    .from('forum_likes')
    .select()
    .match({ user_id: userId, post_id: postId, comment_id: commentId })
    .maybeSingle();

  if (checkError) throw checkError;

  if (existingLike) {
    const { error } = await supabase
      .from('forum_likes')
      .delete()
      .match({ user_id: userId, post_id: postId, comment_id: commentId });

    if (error) throw error;
    return false;
  } else {
    const { error } = await supabase
      .from('forum_likes')
      .insert([{ user_id: userId, post_id: postId, comment_id: commentId }]);

    if (error) throw error;
    return true;
  }
}

// Favoris
export async function toggleBookmark(userId: string, postId: string) {
  const { data: existingBookmark, error: checkError } = await supabase
    .from('forum_bookmarks')
    .select()
    .match({ user_id: userId, post_id: postId })
    .maybeSingle();

  if (checkError) throw checkError;

  if (existingBookmark) {
    const { error } = await supabase
      .from('forum_bookmarks')
      .delete()
      .match({ user_id: userId, post_id: postId });

    if (error) throw error;
    return false;
  } else {
    const { error } = await supabase
      .from('forum_bookmarks')
      .insert([{ user_id: userId, post_id: postId }]);

    if (error) throw error;
    return true;
  }
}

// Abonnements aux mises à jour en temps réel
export function subscribeToForumUpdates(
  onPostUpdate: (payload: any) => void,
  onCommentUpdate: (payload: any) => void
) {
  const postsSubscription = supabase
    .channel('forum_posts_changes')
    .on('postgres_changes', 
      { event: '*', schema: 'public', table: 'forum_posts' },
      onPostUpdate
    )
    .subscribe();

  const commentsSubscription = supabase
    .channel('forum_comments_changes')
    .on('postgres_changes',
      { event: '*', schema: 'public', table: 'forum_comments' },
      onCommentUpdate
    )
    .subscribe();

  return () => {
    postsSubscription.unsubscribe();
    commentsSubscription.unsubscribe();
  };
}